import {
  createAuthInstance,
  createFrontOfficeInstance,
} from '@/shared/api/api-instance';
import { TRequestOptions } from '@/shared/api/types';

export interface IPartnerSummaryDto {
  paymentsTotal: number;
  discountTotal: number;
}

export interface IPartnerSummaryRequestDto {
  from?: string;
  to?: string;
}

export interface IPartnerPaymentsRequestDto extends IPartnerSummaryRequestDto {
  limit?: number;
  offset?: number;
}

export interface IPartnerPaymentDto {
  transactionId?: string;
  orderId?: string;
  email?: string;
  amount?: number | null;
  currency?: string | null;
  createdAt?: string;
}

export interface IPartnerPaymentsResponseDto {
  items: IPartnerPaymentDto[];
  total: number;
}

export interface IPartnerClientsRequestDto {
  search?: string;
}

export interface IPartnerClientDto {
  id: string;
  partnerAccountId: string;
  clientAccountId?: string | null;
  email: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt?: string | null;
}

const buildQuery = (params?: object) => {
  if (!params) {
    return '';
  }

  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      query.set(key, String(value));
    }
  });

  const output = query.toString();

  return output ? `?${output}` : '';
};

/** @deprecated Use `getPartnerAccount` + `getPartnerPayoutsStats`. */
export const getPartnerSummary = (
  params?: IPartnerSummaryRequestDto,
  options?: TRequestOptions
): Promise<IPartnerSummaryDto> => {
  return createFrontOfficeInstance<IPartnerSummaryDto>(
    {
      url: `/api/v1/partner/summary${buildQuery(params)}`,
      method: 'GET',
    },
    options
  );
};

/** @deprecated Use `getPartnerPayouts`. */
export const getPartnerPayments = (
  params?: IPartnerPaymentsRequestDto,
  options?: TRequestOptions
): Promise<IPartnerPaymentsResponseDto> => {
  return createFrontOfficeInstance<IPartnerPaymentsResponseDto>(
    {
      url: `/api/v1/partner/payments${buildQuery(params)}`,
      method: 'GET',
    },
    options
  );
};

/** @deprecated Use `getPartnerClientsExtended`. */
export const getPartnerClients = (
  params?: IPartnerClientsRequestDto,
  options?: TRequestOptions
): Promise<IPartnerClientDto[]> => {
  return createFrontOfficeInstance<IPartnerClientDto[]>(
    {
      url: `/api/v1/partner/clients${buildQuery(params)}`,
      method: 'GET',
    },
    options
  );
};

/** @deprecated Use `postPartnerClientExtended`. */
export const postPartnerClient = (
  data: { email: string; name: string },
  options?: TRequestOptions
): Promise<IPartnerClientDto> =>
  createFrontOfficeInstance<IPartnerClientDto>(
    { url: '/api/v1/partner/clients', method: 'POST', data },
    options
  );

export interface IPartnerLinkDto {
  id: string;
  name: string;
  slug: string;
  url: string;
  isActive: boolean;
  clicksCount: number;
  registrationsCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export const getPartnerLinks = (
  options?: TRequestOptions
): Promise<IPartnerLinkDto[]> =>
  createFrontOfficeInstance<IPartnerLinkDto[]>(
    { url: '/api/v1/partner/links', method: 'GET' },
    options
  );

export const postPartnerLink = (
  data: { name: string },
  options?: TRequestOptions
): Promise<IPartnerLinkDto> =>
  createFrontOfficeInstance<IPartnerLinkDto>(
    { url: '/api/v1/partner/links', method: 'POST', data },
    options
  );

export const patchPartnerLink = (
  id: string,
  data: { name?: string; isActive?: boolean },
  options?: TRequestOptions
): Promise<IPartnerLinkDto> =>
  createFrontOfficeInstance<IPartnerLinkDto>(
    { url: `/api/v1/partner/links/${id}`, method: 'PATCH', data },
    options
  );

export const deletePartnerLink = (
  id: string,
  options?: TRequestOptions
): Promise<void> =>
  createFrontOfficeInstance<void>(
    { url: `/api/v1/partner/links/${id}`, method: 'DELETE' },
    options
  );

// ---------------------------------------------------------------------------
// New partner DTOs (account / transactions / clients-extended / payouts / rules)
// ---------------------------------------------------------------------------

export interface IPartnerAccountDto {
  balanceMinor: number;
  currency: string;
  rates: {
    commissionRate: number;
    partnerDiscountRate: number;
    partnerDiscountNextReviewAt: string;
    wabaBonusRate: number;
    attributionWindowDays: number;
  };
}

export interface IPartnerAccountTransactionDto {
  id: string;
  occurredAt: string;
  amountMinor: number;
  balanceAfterMinor: number;
  currency: string;
  type:
    | 'topup'
    | 'client_subscription_paid'
    | 'referral_commission_credited'
    | 'adjustment'
    | 'refund';
  comment: string | null;
  relatedPaymentId: string | null;
  relatedClientAccountId: string | null;
}

export interface IPartnerAccountTransactionsResponseDto {
  items: IPartnerAccountTransactionDto[];
  total: number;
}

export interface IPartnerClientExtendedDto {
  id: string;
  accountId: string | null;
  /** Human-readable XXXX-XXXX id matching account_support_id; null when client hasn't completed registration. */
  accountSupportId: string | null;
  email: string;
  phone: string | null;
  name: string;
  status: string;
  createdAt: string;
  subscriptionEndsAt: string | null;
  hasAutoPay: boolean | null;
  totalPaidMinor: number | null;
  currency: string;
}

export interface IPartnerClientsExtendedResponseDto {
  items: IPartnerClientExtendedDto[];
  total: number;
}

export interface IPartnerClientsStatsDto {
  payingCount: number;
  anyPaymentCount: number;
}

export interface IPartnerPayoutDto {
  paymentId: string;
  paidAt: string;
  referredAccountId: string;
  referredEmail: string | null;
  paymentType: string;
  amountMinor: number;
  currency: string;
  rewardAmountMinor: number;
  rewardPercent: number;
}

export interface IPartnerPayoutsResponseDto {
  items: IPartnerPayoutDto[];
  total: number;
}

export interface IPartnerPayoutsStatsDto {
  paymentsTotalMinor: number;
  rewardTotalMinor: number;
  currency: string;
}

export interface IPartnerRulesDto {
  commissionRate: number;
  partnerDiscountRate: number;
  wabaBonusRate: number;
  attributionWindowDays: number;
  items: string[];
}

// ---------------------------------------------------------------------------
// New partner endpoint factories
// ---------------------------------------------------------------------------

export const getPartnerAccount = (
  options?: TRequestOptions
): Promise<IPartnerAccountDto> =>
  createFrontOfficeInstance<IPartnerAccountDto>(
    { url: '/api/v1/partner/account', method: 'GET' },
    options
  );

export const getPartnerAccountTransactions = (
  params: { from?: string; to?: string; limit?: number; offset?: number },
  options?: TRequestOptions
): Promise<IPartnerAccountTransactionsResponseDto> =>
  createFrontOfficeInstance<IPartnerAccountTransactionsResponseDto>(
    {
      url: `/api/v1/partner/account/transactions${buildQuery(params)}`,
      method: 'GET',
    },
    options
  );

export const postPartnerAccountTopup = (
  data: { amountMinor: number; method: 'sbp' | 'card' | 'invoice' },
  options?: TRequestOptions
): Promise<{ orderId?: string; invoiceId?: string; paymentUrl?: string }> =>
  createFrontOfficeInstance<{ orderId?: string; invoiceId?: string; paymentUrl?: string }>(
    { url: '/api/v1/partner/account/topup', method: 'POST', data },
    options
  );

export const getPartnerClientsExtended = (
  params: {
    search?: string;
    showInactive?: boolean;
    limit?: number;
    offset?: number;
  },
  options?: TRequestOptions
): Promise<IPartnerClientsExtendedResponseDto> =>
  createFrontOfficeInstance<IPartnerClientsExtendedResponseDto>(
    { url: `/api/v1/partner/clients${buildQuery(params)}`, method: 'GET' },
    options
  );

export const getPartnerClientsStats = (
  options?: TRequestOptions
): Promise<IPartnerClientsStatsDto> =>
  createFrontOfficeInstance<IPartnerClientsStatsDto>(
    { url: '/api/v1/partner/clients/stats', method: 'GET' },
    options
  );

export const postPartnerClientExtended = (
  data: { email: string; phone: string; name: string },
  options?: TRequestOptions
): Promise<IPartnerClientExtendedDto> =>
  createFrontOfficeInstance<IPartnerClientExtendedDto>(
    { url: '/api/v1/partner/clients', method: 'POST', data },
    options
  );

export const getPartnerPayouts = (
  params: { from?: string; to?: string; limit?: number; offset?: number },
  options?: TRequestOptions
): Promise<IPartnerPayoutsResponseDto> =>
  createFrontOfficeInstance<IPartnerPayoutsResponseDto>(
    { url: `/api/v1/partner/payouts${buildQuery(params)}`, method: 'GET' },
    options
  );

export const getPartnerPayoutsStats = (
  params: { from?: string; to?: string },
  options?: TRequestOptions
): Promise<IPartnerPayoutsStatsDto> =>
  createFrontOfficeInstance<IPartnerPayoutsStatsDto>(
    {
      url: `/api/v1/partner/payouts/stats${buildQuery(params)}`,
      method: 'GET',
    },
    options
  );

export const getPartnerRules = (
  options?: TRequestOptions
): Promise<IPartnerRulesDto> =>
  createFrontOfficeInstance<IPartnerRulesDto>(
    { url: '/api/v1/partner/rules', method: 'GET' },
    options
  );

// ---------------------------------------------------------------------------
// Partner impersonation (auth-service via FBS auth proxy).
// FBS routes /api/v1/auth/* to auth-service after stripping /auth, so the
// frontend uses /api/v1/auth/partner/... to reach auth-service's mount at
// /api/v1/partner/... without colliding with partner-service routing.
// ---------------------------------------------------------------------------

export interface IImpersonateResponse {
  token: string;
  expiresAt: string;
  impersonationId: string;
}

export const postPartnerImpersonate = (
  clientAccountId: string,
  options?: TRequestOptions
): Promise<IImpersonateResponse> =>
  createAuthInstance<IImpersonateResponse>(
    {
      url: `/partner/clients/${clientAccountId}/impersonate`,
      method: 'POST',
    },
    options
  );

export const postPartnerImpersonationEnd = (
  impersonationId: string,
  options?: TRequestOptions
): Promise<void> =>
  createAuthInstance<void>(
    {
      url: `/partner/impersonation/${impersonationId}/end`,
      method: 'POST',
    },
    options
  );
